import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import type {
  TripDetailResponseDto,
  ChatMessageResponseDto,
  ChatSessionResponseDto,
  ChatResponseWithActions,
  ShareResponseDto,
} from "@tabipla/shared";
import { ApiError, type TripResponseDto } from "@tabipla/shared";
import { packingKeys } from "./usePacking";

// Fetch all trips
export function useTrips() {
  return useQuery({
    queryKey: ["trips"],
    queryFn: async () => {
      const response = await apiClient.get<TripResponseDto[]>("/api/trips");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch trips");
      }
      return response.data ?? [];
    },
  });
}

// Fetch single trip with itineraries
export function useTrip(tripId: string | undefined) {
  return useQuery({
    queryKey: ["trips", tripId],
    queryFn: async () => {
      if (!tripId) throw new Error("Trip ID is required");
      const response = await apiClient.get<TripDetailResponseDto>(`/api/trips/${tripId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch trip");
      }
      return response.data;
    },
    enabled: !!tripId,
  });
}

// Create trip
interface CreateTripInput {
  title: string;
  destination: string;
  startDate: string | null;
  endDate: string | null;
  budget?: number | null;
  interests?: string[];
  groupId?: string;
  transportMode?: "walking" | "public_transit" | "car";
}

export function useCreateTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTripInput) => {
      const response = await apiClient.post<TripResponseDto>("/api/trips", input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create trip");
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      // グループから計画を作成した場合、グループの計画一覧も無効化
      if (data?.groupId) {
        queryClient.invalidateQueries({ queryKey: ["groups", data.groupId, "trips"] });
      }
    },
  });
}

// Update trip
interface UpdateTripInput {
  title?: string;
  destination?: string;
  startDate?: string;
  endDate?: string;
  budget?: number | null;
  interests?: string[];
  status?: "planning" | "confirmed" | "completed";
  coverEmoji?: string | null;
  coverColor?: string | null;
  transportMode?: "walking" | "public_transit" | "car" | null;
}

export function useUpdateTrip(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateTripInput) => {
      const response = await apiClient.put<TripResponseDto>(`/api/trips/${tripId}`, input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update trip");
      }
      return response.data;
    },
    onMutate: async (input) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["trips"] });
      await queryClient.cancelQueries({ queryKey: ["trips", tripId] });

      // Snapshot previous values
      const previousTrips = queryClient.getQueryData<TripResponseDto[]>(["trips"]);
      const previousTrip = queryClient.getQueryData<TripDetailResponseDto>(["trips", tripId]);
      const groupId = previousTrip?.groupId;

      // Optimistically update trips list
      if (previousTrips) {
        queryClient.setQueryData<TripResponseDto[]>(
          ["trips"],
          previousTrips.map((trip) =>
            trip.id === tripId ? { ...trip, ...input, updatedAt: new Date().toISOString() } : trip
          )
        );
      }

      // Optimistically update individual trip
      if (previousTrip) {
        queryClient.setQueryData<TripDetailResponseDto>(["trips", tripId], {
          ...previousTrip,
          ...input,
          updatedAt: new Date().toISOString(),
        });
      }

      return { previousTrips, previousTrip, groupId };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousTrips) {
        queryClient.setQueryData(["trips"], context.previousTrips);
      }
      if (context?.previousTrip) {
        queryClient.setQueryData(["trips", tripId], context.previousTrip);
      }
    },
    onSettled: (_, __, ___, context) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
      // グループに属している計画の場合、グループの計画一覧も無効化
      if (context?.groupId) {
        queryClient.invalidateQueries({ queryKey: ["groups", context.groupId, "trips"] });
      }
    },
  });
}

// Delete trip
export function useDeleteTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tripId: string) => {
      const response = await apiClient.delete<null>(`/api/trips/${tripId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete trip");
      }
      return tripId;
    },
    onMutate: async (tripId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["trips"] });

      // Snapshot the previous value
      const previousTrips = queryClient.getQueryData<TripResponseDto[]>(["trips"]);

      // 削除対象の計画の groupId を保存（後でグループの計画一覧を無効化するため）
      const deletedTrip = previousTrips?.find((trip) => trip.id === tripId);
      const groupId = deletedTrip?.groupId;

      // Optimistically remove the trip from the list
      if (previousTrips) {
        queryClient.setQueryData<TripResponseDto[]>(
          ["trips"],
          previousTrips.filter((trip) => trip.id !== tripId)
        );
      }

      return { previousTrips, groupId };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousTrips) {
        queryClient.setQueryData(["trips"], context.previousTrips);
      }
    },
    onSettled: (_, __, ___, context) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      // グループに属していた計画の場合、グループの計画一覧も無効化
      if (context?.groupId) {
        queryClient.invalidateQueries({ queryKey: ["groups", context.groupId, "trips"] });
      }
    },
  });
}

// Generate itinerary
export function useGenerateItinerary(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (context?: {
      departureLocation?: string;
      travelStyle?: "relaxed" | "active" | "balanced";
      additionalNotes?: string;
    }) => {
      const response = await apiClient.post<TripDetailResponseDto>(
        `/api/trips/${tripId}/generate`,
        context ?? {}
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to generate itinerary");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// Regenerate itinerary for a specific day
interface RegenerateDayInput {
  dayNumber: number;
  preference?: "morning" | "afternoon" | "evening";
}

export function useRegenerateDay(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dayNumber, preference }: RegenerateDayInput) => {
      const response = await apiClient.post<TripDetailResponseDto>(
        `/api/trips/${tripId}/regenerate/${dayNumber}`,
        preference ? { preference } : {}
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to regenerate day");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// ============================================
// Chat Session Hooks
// ============================================

// Fetch all chat sessions for a trip
export function useChatSessions(tripId: string | undefined) {
  return useQuery({
    queryKey: ["trips", tripId, "sessions"],
    queryFn: async () => {
      if (!tripId) throw new Error("Trip ID is required");
      const response = await apiClient.get<ChatSessionResponseDto[]>(
        `/api/trips/${tripId}/sessions`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch chat sessions");
      }
      return response.data ?? [];
    },
    enabled: !!tripId,
  });
}

// Create a new chat session
export function useCreateChatSession(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (title?: string) => {
      const response = await apiClient.post<ChatSessionResponseDto>(
        `/api/trips/${tripId}/sessions`,
        title ? { title } : {}
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create chat session");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId, "sessions"] });
    },
  });
}

// Delete a chat session
export function useDeleteChatSession(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiClient.delete<null>(`/api/trips/${tripId}/sessions/${sessionId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete chat session");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId, "sessions"] });
    },
  });
}

// Fetch messages for a specific session
export function useChatMessages(tripId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: ["trips", tripId, "sessions", sessionId, "messages"],
    queryFn: async () => {
      if (!tripId || !sessionId) throw new Error("Trip ID and Session ID are required");
      const response = await apiClient.get<ChatMessageResponseDto[]>(
        `/api/trips/${tripId}/sessions/${sessionId}/messages`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch messages");
      }
      return response.data ?? [];
    },
    enabled: !!tripId && !!sessionId,
  });
}

// Send a chat message in a session
export function useSendChatMessage(tripId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (message: string) => {
      const response = await apiClient.post<ChatResponseWithActions>(
        `/api/trips/${tripId}/sessions/${sessionId}/chat`,
        { message }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to send message");
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate messages for this session
      queryClient.invalidateQueries({
        queryKey: ["trips", tripId, "sessions", sessionId, "messages"],
      });
      // Invalidate sessions list to update title if changed
      queryClient.invalidateQueries({ queryKey: ["trips", tripId, "sessions"] });
      // If actions were executed, invalidate trip data to refresh itineraries
      if (data?.actions && data.actions.length > 0) {
        queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
      }
      // If packing actions were executed, invalidate packing data
      if (data?.packingActions && data.packingActions.length > 0) {
        queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
        queryClient.invalidateQueries({ queryKey: packingKeys.progress(tripId) });
      }
    },
  });
}

// Send a chat message, creating a new session if needed
export function useSendChatMessageWithAutoSession(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      message,
      sessionId,
    }: {
      message: string;
      sessionId: string | null;
    }): Promise<{ sessionId: string; response: ChatResponseWithActions | undefined }> => {
      let targetSessionId = sessionId;

      // Create a new session if none exists
      if (!targetSessionId) {
        const createResponse = await apiClient.post<ChatSessionResponseDto>(
          `/api/trips/${tripId}/sessions`,
          {}
        );
        if (!createResponse.success || !createResponse.data) {
          throw ApiError.fromResponse(createResponse.error, "Failed to create chat session");
        }
        targetSessionId = createResponse.data.id;
      }

      // Send the message
      const response = await apiClient.post<ChatResponseWithActions>(
        `/api/trips/${tripId}/sessions/${targetSessionId}/chat`,
        { message }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to send message");
      }

      return { sessionId: targetSessionId, response: response.data };
    },
    onSuccess: ({ sessionId, response }) => {
      // Invalidate messages for this session
      queryClient.invalidateQueries({
        queryKey: ["trips", tripId, "sessions", sessionId, "messages"],
      });
      // Invalidate sessions list
      queryClient.invalidateQueries({ queryKey: ["trips", tripId, "sessions"] });
      // If actions were executed, invalidate trip data to refresh itineraries
      if (response?.actions && response.actions.length > 0) {
        queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
      }
      // If packing actions were executed, invalidate packing data
      if (response?.packingActions && response.packingActions.length > 0) {
        queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
        queryClient.invalidateQueries({ queryKey: packingKeys.progress(tripId) });
      }
    },
  });
}

// ============================================
// Share Hooks (Booklet)
// ============================================

// Generate share link
export function useShareTrip(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ShareResponseDto>(`/api/trips/${tripId}/share`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to generate share link");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}

// Disable share link
export function useUnshareTrip(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete<null>(`/api/trips/${tripId}/share`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to disable share link");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
    },
  });
}
